import { PM_MENU_ACCESS_KEYS } from '~/utils/constant';
import { BaseRestRepository } from './baseRestRepository';
import { permissionsDefinitionRepository } from './permissionsDefinitionRepository';
import type {
  AddPropertyManagerPropertyAccessDto,
  PropertyManagerPermissionsDto,
  PropertyManagerPropertyAccessDto,
  PropertyManagerMenuAccessDto,
  UpdatePropertyManagerMenuAccessDto,
  RemovePropertyManagerPropertyAccessDto,
} from '~/swagger/portal/Api';

const PROPERTY_MANAGERS_BASE_URL = '/api/property-managers';

export class PropertyManagerPermissionsRepository extends BaseRestRepository {
  static latestPropertyAccess: PropertyManagerPropertyAccessDto | undefined;

  public async getPermissions(propertyManagerId: string): Promise<PropertyManagerPermissionsDto> {
    const response = await this.fetchWithAuth(`${PROPERTY_MANAGERS_BASE_URL}/${propertyManagerId}/permissions`);
    return await this.getJsonResponse(response);
  }

  public async addPermission(propertyManagerId: string, permission: string): Promise<PropertyManagerPermissionsDto> {
    await permissionsDefinitionRepository.assertPermissionExists(permission);
    const response = await this.fetchWithAuth(`${PROPERTY_MANAGERS_BASE_URL}/${propertyManagerId}/permissions`, {
      method: 'POST',
      body: JSON.stringify({ permission }),
    });
    return await this.getJsonResponse(response);
  }

  public async removePermission(propertyManagerId: string, permission: string): Promise<PropertyManagerPermissionsDto> {
    const response = await this.fetchWithAuth(`${PROPERTY_MANAGERS_BASE_URL}/${propertyManagerId}/permissions`, {
      method: 'DELETE',
      body: JSON.stringify({ permission }),
    });
    return await this.getJsonResponse(response);
  }

  public async getMenuAccess(propertyManagerId: string): Promise<PropertyManagerMenuAccessDto> {
    const response = await this.fetchWithAuth(`${PROPERTY_MANAGERS_BASE_URL}/${propertyManagerId}/menu-access`);
    const data = await this.getJsonResponse(response);

    if (data.menuAccess && data.menuAccess.length === 1 && data.menuAccess[0] === '*') {
      data.menuAccess = [...PM_MENU_ACCESS_KEYS];
    }

    return data;
  }

  public async updateMenuAccess(
    propertyManagerId: string,
    access: UpdatePropertyManagerMenuAccessDto
  ): Promise<PropertyManagerMenuAccessDto> {
    const response = await this.fetchWithAuth(`${PROPERTY_MANAGERS_BASE_URL}/${propertyManagerId}/menu-access`, {
      method: 'PUT',
      body: JSON.stringify(access),
    });
    return await this.getJsonResponse(response);
  }

  public async getPropertyAccess(propertyManagerId: string): Promise<PropertyManagerPropertyAccessDto> {
    const response = await this.fetchWithAuth(`${PROPERTY_MANAGERS_BASE_URL}/${propertyManagerId}/property-access`);
    const result = await this.getJsonResponse(response);

    PropertyManagerPermissionsRepository.latestPropertyAccess = result;

    return result;
  }

  public async addPropertyAccess(
    propertyManagerId: string,
    access: AddPropertyManagerPropertyAccessDto
  ): Promise<PropertyManagerPropertyAccessDto> {
    const response = await this.fetchWithAuth(`${PROPERTY_MANAGERS_BASE_URL}/${propertyManagerId}/property-access`, {
      method: 'POST',
      body: JSON.stringify(access),
    });
    return await this.getJsonResponse(response);
  }

  public async removePropertyAccess(
    propertyManagerId: string,
    access: RemovePropertyManagerPropertyAccessDto
  ): Promise<PropertyManagerPropertyAccessDto> {
    const response = await this.fetchWithAuth(`${PROPERTY_MANAGERS_BASE_URL}/${propertyManagerId}/property-access`, {
      method: 'DELETE',
      body: JSON.stringify(access),
    });
    return await this.getJsonResponse(response);
  }

  public async setPropertyAccessBatch(
    propertyManagerId: string,
    newPortfolioIds: string[],
    newPropertyIds: string[],
    allProperties: boolean = false
  ): Promise<void> {
    const current = PropertyManagerPermissionsRepository.latestPropertyAccess;

    if (allProperties && !current?.allProperties) {
      await this.addPropertyAccess(propertyManagerId, { allProperties: true });
      return;
    }

    if (!allProperties && current?.allProperties) {
      await this.removePropertyAccess(propertyManagerId, { allProperties: true });
    }

    const currentPortfolioIds = current?.portfolioIds || [];
    const currentPropertyIds = current?.propertyIds || [];

    const portfoliosToRemove = currentPortfolioIds.filter((id) => !newPortfolioIds.includes(id));
    const portfoliosToAdd = newPortfolioIds.filter((id) => !currentPortfolioIds.includes(id));
    const propertiesToRemove = currentPropertyIds.filter((id) => !newPropertyIds.includes(id));
    const propertiesToAdd = newPropertyIds.filter((id) => !currentPropertyIds.includes(id));

    const hasChanges =
      portfoliosToRemove.length > 0 || propertiesToRemove.length > 0 || portfoliosToAdd.length > 0 || propertiesToAdd.length > 0;

    if (!hasChanges) {
      return;
    }

    for (const portfolioId of portfoliosToRemove) {
      await this.removePropertyAccess(propertyManagerId, { allProperties: false, portfolioId });
    }

    for (const propertyId of propertiesToRemove) {
      await this.removePropertyAccess(propertyManagerId, { allProperties: false, propertyId });
    }

    for (const portfolioId of portfoliosToAdd) {
      await this.addPropertyAccess(propertyManagerId, { allProperties: false, portfolioId });
    }

    for (const propertyId of propertiesToAdd) {
      await this.addPropertyAccess(propertyManagerId, { allProperties: false, propertyId });
    }
  }
}

export const propertyManagerPermissionsRepository = new PropertyManagerPermissionsRepository();
